<template>
  <Transition name="fade">
    <div
      v-if="visible"
      class="user-manual-overlay"
      role="dialog"
      aria-label="User Manual"
      aria-modal="true"
      @keydown="handleKeydown"
    >
      <div class="manual-toolbar">
        <h2 class="manual-toolbar-title">
          <span class="material-symbols-outlined manual-toolbar-icon">menu_book</span>
          User Manual
        </h2>
        <div class="manual-toolbar-actions">
          <button
            class="manual-btn manual-btn-outline"
            @click="printManual"
            title="Print this manual"
          >
            <span class="material-symbols-outlined">print</span>
            Print
          </button>
          <button
            class="manual-btn manual-btn-outline"
            @click="openInNewTab"
            title="Open in new browser tab"
          >
            <span class="material-symbols-outlined">open_in_new</span>
            Open in New Tab
          </button>
          <button
            ref="closeButton"
            class="manual-btn manual-btn-close"
            @click="$emit('close')"
            aria-label="Close user manual"
          >
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>
      </div>
      <iframe
        ref="manualFrame"
        :src="manualSrc"
        class="manual-iframe"
        title="User Manual"
        @load="onFrameLoad"
      ></iframe>
    </div>
  </Transition>
</template>

<script>
export default {
  name: 'UserManualOverlay',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close'],
  data() {
    return {
      manualSrc: '/docs/index.html',
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.lockScroll()
        this.$nextTick(() => {
          if (this.$refs.closeButton) {
            this.$refs.closeButton.focus()
          }
        })
      } else {
        this.unlockScroll()
      }
    },
  },
  methods: {
    lockScroll() {
      document.body.style.overflow = 'hidden'
    },
    unlockScroll() {
      document.body.style.overflow = ''
    },
    openInNewTab() {
      const manualUrl = new URL('/docs/index.html', window.location.origin)
      window.open(manualUrl.toString(), '_blank', 'noopener,noreferrer')
    },
    printManual() {
      const frame = this.$refs.manualFrame
      if (frame && frame.contentWindow) {
        frame.contentWindow.focus()
        frame.contentWindow.print()
      }
    },
    onFrameLoad() {
      const hash = window.location.hash
      if (hash && this.$refs.manualFrame) {
        try {
          const iframeDoc = this.$refs.manualFrame.contentDocument || this.$refs.manualFrame.contentWindow.document
          const el = iframeDoc.querySelector(hash)
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        } catch (e) {
          // cross-origin — ignore
        }
      }
    },
    handleKeydown(e) {
      if (e.key === 'Escape') {
        this.$emit('close')
      }
    },
  },
}
</script>

<style scoped>
.user-manual-overlay {
  position: fixed;
  inset: 0;
  z-index: 1060;
  background: #fff8f3;
  display: flex;
  flex-direction: column;
}

.manual-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  background: #fff;
  border-bottom: 1px solid #d1c5b6;
  flex-shrink: 0;
  min-height: 56px;
}

.manual-toolbar-title {
  font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #775928;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
}

.manual-toolbar-icon {
  font-size: 24px;
}

.manual-toolbar-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.manual-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  font-family: 'Manrope', system-ui, sans-serif;
  font-size: 13px;
  font-weight: 600;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  transition: background 0.15s ease;
}

.manual-btn-outline {
  background: transparent;
  color: #775928;
  border: 1px solid #d1c5b6;
}

.manual-btn-outline:hover {
  background: #f5ece5;
  border-color: #775928;
}

.manual-btn-close {
  width: 40px;
  height: 40px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5ece5;
  color: #1e1b17;
  border-radius: 50%;
}

.manual-btn-close:hover {
  background: #efe7e0;
}

.manual-iframe {
  flex: 1;
  width: 100%;
  border: none;
  background: #fff8f3;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@media (max-width: 768px) {
  .manual-toolbar {
    padding: 10px 16px;
  }

  .manual-toolbar-title {
    font-size: 15px;
  }

  .manual-btn-outline {
    font-size: 11px;
    padding: 6px 12px;
  }
}

@media print {
  .manual-toolbar {
    display: none !important;
  }

  .user-manual-overlay {
    background: #fff;
  }
}
</style>
